/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { basicDialogSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor, setErrorMsg } from "../../../store/helpers/helperReducers";
import Draggable from "react-draggable";
import { PaperProps, Paper } from "@mui/material";
import { iconButtonSxProps } from "../../helpers";
import { setIsEditTask, setScheduledTask } from "../../../store/api/tasks/reducer";
import { useLocation } from "react-router-dom";
import { isEditTaskSelector } from "../../../store/api/tasks/selector";


function PaperComponent(props: PaperProps) {
  return (
    <Draggable handle="#basic-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
    borderColor: "var(--border)",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root.MuiDialog-paper": {
    backgroundColor: "var(--card)",
    color: "var(--foreground)",
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
  isColorHeader?: boolean;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, isColorHeader = false, ...other } = props;

  return (
    <DialogTitle
      sx={{
        m: 0,
        pl: 7.5,
        pr: 3,
        pt: 3,
        pb: 3,
        cursor: "move",
        color: isColorHeader ? `var(--primary-foreground)` : "inherit",
        backgroundColor: isColorHeader ? "var(--primary)" : "inherit",
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "var(--muted-foreground)",
            ...iconButtonSxProps,
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

function BootstrapDialogVersionTitle(props: DialogTitleProps) {
  const { onClose, ...other } = props;

  return (
    <IconButton
      aria-label="close"
      onClick={onClose}
      sx={{
        position: "absolute",
        right: 8,
        top: 8,
        color: (theme) => theme.palette.grey[500],
      }}
    >
      <CloseIcon />
    </IconButton>
  );
}

interface IBasicDilog {
  headerTitle: string;
  children?: React.ReactNode;
  dialogActions?: React.ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  version?: boolean;
  headerTheme?: boolean;
  onClose?: () => void;
}

const BasicDialog: React.FC<IBasicDilog> = ({ children, headerTitle, dialogActions, size = "sm", version = false, headerTheme = false, onClose }) => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const isTaskRoute = location?.pathname.split("/")[1]?.toLowerCase() === "tasks" ? true : false;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(size));
  const basicDialodOpenFor = useSelector(basicDialogSelector);
  const isEditTask = useSelector(isEditTaskSelector);
  const handleClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
    if (isTaskRoute) {
      dispatch(setScheduledTask({}));
      dispatch(setErrorMsg({}));
      dispatch(setIsEditTask(!isEditTask));
    }
    if (onClose) onClose();
  };

  const handleDlgClose = (event: any, reason: any) => {
    if (reason && reason === "backdropClick") {
      return;
    }
    if (reason === "escapeKeyDown") {
      onClose?.();
    }
    dispatch(setBasicDialogOpenFor(undefined));
  };

  React.useEffect(() => {
    dispatch(setErrorMsg({}));
  }, [dispatch]);

  return (
    <div>
      <BootstrapDialog
        onClose={handleDlgClose}
        aria-labelledby="basic-dialog-title"
        open={!!basicDialodOpenFor}
        fullScreen={fullScreen}
        fullWidth
        maxWidth={size}
        PaperComponent={PaperComponent}
        sx={{ zIndex: 2000 }}
      >
        {version === false ? (
          <BootstrapDialogTitle id="basic-dialog-title" onClose={handleClose} isColorHeader={headerTheme}>
            {headerTitle}
          </BootstrapDialogTitle>
        ) : (
          <BootstrapDialogVersionTitle id="basic-dialog-title" onClose={handleClose} />
        )}
        <DialogContent dividers>{children}</DialogContent>
        {dialogActions ? <DialogActions sx={{ justifyContent: "flex-end" }}>{dialogActions}</DialogActions> : ""}
      </BootstrapDialog>
    </div>
  );
};

export default BasicDialog;
